import type { CustomIcon } from '@/components/common/interfaces/CustomIcon';
import { addOpacityToRgb, DATE_FORMAT_5, DATE_FORMAT_6, formattedDate, getRandomRgbColor } from '@/utils';
import { DateTime } from 'luxon';

export interface NetworkGraph {
  identifier: string;
  values: NetworkGraphValues;
  timestamp: string;

  surveyTitle: string;
  chartType: string;
  fieldName: string;
  choices?: {
    [key: string]: Choice;
  };
  data?: {
    [key: string]: Choice;
  };
  frequencyData?: {
    [key: string]: FrequencyData;
  };
}

export interface NetworkGraphValues {
  survey: string;
  field?: string;
  chartType: string;
  investigation: string;
  dateType: string;
  dateSelectionStart?: string;
  dateSelectionEnd?: string;
}

interface Choice {
  label?: string;
  timestamp?: string;
  value?: number;
  count?: number;
  dates?: { day: string, count: number}[];
}

interface FrequencyData {
  data: { timestamp: string }[];
  dates: { day: string, count: number}[];
  name: string;
}

/**
 * One analytics series, in the shape `mapGraphFromBackend` below builds and
 * `analyticsChartOption.ts` reads.
 *
 * It is Chart.js-shaped because that is what the analytics endpoints have always been mapped
 * into, and ENG-8153 ported the *renderer* to ECharts rather than reshaping the mapping. What
 * that port did take out are the fields only Chart.js ever read: `tooltipLabel`, `legendGroup`,
 * `fill`, `barPercentage`, the four `point*` styles, and `type`. The perfusion graphs carried
 * those, and they have their own series types now (`perfusionGraphData.ts`), so nothing was
 * left reading them here.
 */
export interface GraphDataSet {
  label: string;
  data: number[];
  /** Per-slice on a pie and per-bar on a bar chart; unused on a line. */
  backgroundColor?: string[];
  /** Per-point on a bar chart; the whole line's colour on a line chart. */
  borderColor?: string[];
  borderWidth?: number;
  /** Chart.js's curve tension. Any value above zero draws the line smoothed. */
  tension?: number;
}

export interface GraphData {
  labels: string[] | number[];
  datasets: GraphDataSet[];
}

export interface GraphTypeModel {
  type: string;
  label: string;
  icon: CustomIcon;
}

// eslint-disable-next-line no-shadow
export enum GraphType {
  PIE='pie',
  BAR='bar',
  LINE='line',
  LINE_CHOICE='lineChoice',
  FREQUENCY='frequency',
  BASIC='basic'
}

interface IGraph {
    identifier: string;
    formIdentifier: string;
    formName?: string;
    fieldIdentifier?: string;
    fieldName?: string;
    chartType?: string;
    createdAt: Date;
    dateType?: string;
    dateSelectionStart?: string;
    dateSelectionEnd?: string;

    loading?: boolean;
}

export class Graph {
  identifier: string;
  formIdentifier: string;
  formName?: string;
  fieldIdentifier?: string;
  fieldName?: string;
  chartType?: string;
  choices?: {
    [key: string]: Choice;
  };
  createdAt: Date;
  graphData?: GraphData;
  dateType?: string;
  dateSelectionStart?: string;
  dateSelectionEnd?: string;

  loading?: boolean;

  constructor(graph: IGraph) {
    this.identifier = graph.identifier;
    this.formName = graph.formName;
    this.fieldName = graph.fieldName;
    this.formIdentifier = graph.formIdentifier;
    this.fieldIdentifier = graph.fieldIdentifier;
    this.loading = graph.loading;
    this.createdAt = graph.createdAt;
    this.dateType = graph.dateType;
    this.dateSelectionStart = graph.dateSelectionStart;
    this.dateSelectionEnd = graph.dateSelectionEnd;
  }

  static mapFrombackend(graph: NetworkGraph) {
    return new Graph({
      identifier: graph.identifier,
      formIdentifier: graph.values.survey,
      fieldIdentifier: graph.values.field,
      loading: true,
      createdAt: DateTime.fromISO(graph.timestamp).toJSDate(),
      dateType: graph.values.dateType,
      dateSelectionStart: graph.values.dateSelectionStart,
      dateSelectionEnd: graph.values.dateSelectionEnd
    });
  }

  static mapGraphFromBackend(graph: Graph, networkGraph: NetworkGraph) {
    graph.formName = networkGraph.surveyTitle;
    graph.fieldName = networkGraph.fieldName;
    graph.chartType = networkGraph.chartType;
    graph.choices = networkGraph.choices ?? networkGraph.data;
    graph.loading = false;

    if (graph.chartType === GraphType.BAR || graph.chartType === GraphType.PIE || graph.chartType === GraphType.LINE) {
      if (graph.choices) {
        const labels: string[] = [];
        const values: number[] = [];
        Object.keys(graph.choices).forEach(choiceKey => {
          const choice = graph.choices![choiceKey];

          if (graph.chartType == GraphType.LINE) {
            labels.push(formattedDate(DateTime.fromISO(choice.timestamp!).toJSDate(), DATE_FORMAT_5));
            if (choice.value != undefined) {
              values.push(choice.value);
            }
          } else {
            labels.push(choice.label!);
            if (choice.count != undefined) {
              values.push(choice.count);
            } else if (choice.value != undefined) {
              values.push(choice.value);
            }
          }
        });

        const dataset: GraphDataSet = {
          label: graph.fieldName,
          data: values,
          borderWidth: 1
        };

        if (graph.chartType == GraphType.LINE) {
          dataset.tension = 0.4;
        }

        const backgroundColors: string[] = [];
        const borderColors: string[] = [];

        dataset.data.forEach(() => {
          const color = getRandomRgbColor();
          borderColors.push(`${color}`);
          backgroundColors.push(`${addOpacityToRgb(color, 0.5)}`);
        });

        dataset.backgroundColor = backgroundColors;
        dataset.borderColor = borderColors;

        const graphData: GraphData = {
          labels,
          datasets: [dataset]
        };

        graph.graphData = graphData;
      }
    }

    if (graph.chartType === GraphType.FREQUENCY) {
      graph.chartType = GraphType.LINE;
      graph.fieldName = 'Frequency of entries over time';

      const datasets: GraphDataSet[] = [];

      const labels: string[] = [];
      Object.keys(networkGraph.frequencyData!).forEach(key => {
        const userObject = networkGraph.frequencyData![key];

        const values: number[] = [];

        userObject.dates.forEach(date => {
          const dateLabel = formattedDate(DateTime.fromISO(date.day).toJSDate(), DATE_FORMAT_6);
          if (!labels.includes(dateLabel)) {
            labels.push(dateLabel);
          }

          values.push(date.count);
        });

        datasets.push({
          label: userObject.name,
          data: values,
          borderWidth: 1,
          borderColor: [getRandomRgbColor()],
          tension: 0.4
        });

      });

      graph.graphData = {
        labels,
        datasets
      };
    }

    if (graph.chartType === GraphType.LINE_CHOICE) {
      graph.chartType = GraphType.LINE;

      const datasets: GraphDataSet[] = [];

      const labels: string[] = [];
      Object.keys(networkGraph.choices!).forEach(key => {
        const choiceObj = networkGraph.choices![key];

        const values: number[] = [];
        choiceObj.dates?.forEach(date => {
          const dateLabel = formattedDate(DateTime.fromISO(date.day).toJSDate(), DATE_FORMAT_6);
          if (!labels.includes(dateLabel)) {
            labels.push(dateLabel);
          }

          values.push(date.count);
        });


        datasets.push({
          label: choiceObj.label!,
          data: values,
          borderWidth: 1,
          borderColor: [getRandomRgbColor()],
          tension: 0.4
        });
      });

      graph.graphData = {
        labels: labels.sort(),
        datasets
      };
    }
  }
}
